import { ReactElement, useEffect, useState } from 'react'
import { Price } from '../../../../../../src/ui-components/products/Price'
import { Thumbnail } from '../../../../../../src/ui-components/products/Thumbnail'
import { I18n } from 'common'
import { defaultTheme } from '../../../../../../src/theme/index'
import { breakpoints } from '../../../../../../src/theme/breakpoints'
import styled from 'styled-components'

import { ImageBypartAPIResponse } from '../../../../../../src/api/searchResults/interfaces'
import {
  getImageUrl,
  getPartImages,
} from '../../../../../../src/helpers/partImageHelpers'
import { useNavigate } from 'react-router-dom'
import { ISeller } from '../../../../../../src/services/MultiSellerServicesProvider'
import { BundledTag } from 'src/features/cart/components/BundledTag'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'

export interface PartDetail {
  thumbnailUrl?: string
  description: string
  partNumber: string
  lineCode: string
  brand: string
  location: string
  yourCost: number
  corePrice?: number
  listPrice: number
  qty: number
  total: number
  snCalled: string
  bundleDiscount?: number
  bundlePrice?: number
  bundled?: boolean
}

export interface PartsTableContentProps {
  parts: Array<PartDetail>
  showYourCost: boolean
  seller: ISeller
}

export const PartsTableContent = (
  props: PartsTableContentProps
): ReactElement => {
  const { parts, showYourCost, seller } = props
  const [partImages, setPartImages] = useState<ImageBypartAPIResponse[]>()
  const navigate = useNavigate()
  const uiStore = useUiStateStore()

  useEffect(() => {
    getPartImages({ parts }).then((resp) => {
      setPartImages(resp)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goToProductDetails = (part: PartDetail) => {
    navigate(
      `/product/?partNumber=${part.partNumber}&lineCode=${part.lineCode}&coverageKey=${seller.coverageKey}`
    )
  }

  return (
    <tbody>
      {parts.map((part) => (
        <Row key={part?.partNumber}>
          <td
            align="center"
            style={{ paddingTop: '20px', paddingBottom: '20px' }}
          >
            <Thumbnail
              urlOrBase64={getImageUrl({
                linecode: part?.lineCode,
                partnumber: part?.partNumber,
                partImages,
              })}
              width={90}
              height={90}
              placeholder
            />
          </td>
          <Column align="left" width={180}>
            <div style={{ flexDirection: 'column', display: 'flex' }}>
              <StyledText
                bold
                underline
                pointer
                wrap
                onClick={() => goToProductDetails(part)}
              >
                {part?.description}
              </StyledText>
              <LabelWrapper>
                <RightContentSpan>
                  <I18nComponent name="partNum" />:
                </RightContentSpan>
                <StyledText>{part?.partNumber}</StyledText>
              </LabelWrapper>
              <LabelWrapper>
                <RightContentSpan>
                  <I18nComponent name="lineCode" />:
                </RightContentSpan>
                <StyledText>{part?.lineCode}</StyledText>
              </LabelWrapper>
              {/* //Details Mobile Version */}
              <DetailsValueMobile>
                <LabelWrapperMobile>
                  <I18nComponent name="location" />:
                  <StyledTextMobile>
                    {part.snCalled || part?.location}
                  </StyledTextMobile>
                </LabelWrapperMobile>
                {showYourCost && (
                  <>
                    <LabelWrapperMobile>
                      <I18nComponent name="yourCost" />:
                      <StyledTextMobile>
                        {part?.bundled ? (
                          <>
                            <Price num={part?.bundlePrice} />
                            <PreviousPrice>
                              <Price num={part?.yourCost} />
                            </PreviousPrice>
                          </>
                        ) : (
                          <Price num={part?.yourCost} />
                        )}
                      </StyledTextMobile>
                    </LabelWrapperMobile>

                    {uiStore?.showBundles() && part?.bundled && (
                      <MobileBundleWrapper>
                        <BundledTag />
                      </MobileBundleWrapper>
                    )}
                  </>
                )}

                <LabelWrapperMobile>
                  <LeftCrossMobile>+ </LeftCrossMobile>
                  <I18n name="listPrice" /> :
                  <StyledTextMobile>
                    <Price num={part?.listPrice} />
                  </StyledTextMobile>
                </LabelWrapperMobile>
                <LabelWrapperMobile>
                  <I18nComponent name="qty" />:
                  <StyledTextMobile>{part?.qty}</StyledTextMobile>
                </LabelWrapperMobile>
                {showYourCost && (
                  <StyledText bold>
                    <I18nComponent name="total" />:
                    <StyledTextMobile>
                      <Price
                        num={
                          (part?.yourCost + (part?.corePrice ?? 0)) * part?.qty
                        }
                      />
                    </StyledTextMobile>
                  </StyledText>
                )}
              </DetailsValueMobile>
            </div>
          </Column>

          {/* //Desktop Version */}
          <ColumnDesktop align="left">
            <LocationCell>
              <StyledText bold>{part.snCalled || part.location}</StyledText>
            </LocationCell>
          </ColumnDesktop>
          {showYourCost && (
            <ColumnDesktop align="left">
              <PriceWrapper>
                {part?.bundled ? (
                  <>
                    <StyledText bold>
                      <Price num={part?.bundlePrice} />
                    </StyledText>
                    <PreviousPrice>
                      <Price num={part?.yourCost} />
                    </PreviousPrice>
                  </>
                ) : (
                  <StyledText bold>
                    <Price num={part?.yourCost} />
                  </StyledText>
                )}
                {uiStore?.showBundles() && part?.bundled && <BundledTag />}
              </PriceWrapper>
            </ColumnDesktop>
          )}
          <ColumnDesktop align="left">
            <StyledText bold>
              <Price num={part?.listPrice} />
            </StyledText>
          </ColumnDesktop>
          <ColumnDesktop align="left">
            <StyledText bold>{part?.qty}</StyledText>
          </ColumnDesktop>
          {showYourCost && (
            <ColumnDesktop align="left">
              {part?.bundled && (
                <StyledText bold block>
                  <Price
                    num={
                      ((part?.bundlePrice ?? 0) + (part?.corePrice ?? 0)) *
                      part?.qty
                    }
                  />
                </StyledText>
              )}
              {part?.bundled ? (
                <StyledText lineThrough block>
                  <Price
                    num={(part?.yourCost + (part?.corePrice ?? 0)) * part?.qty}
                  />
                </StyledText>
              ) : (
                <StyledText bold block>
                  <Price
                    num={(part?.yourCost + (part?.corePrice ?? 0)) * part?.qty}
                  />
                </StyledText>
              )}
            </ColumnDesktop>
          )}
        </Row>
      ))}
    </tbody>
  )
}

interface RowProps {
  height?: string
}
const Row = styled.tr<RowProps>`
  width: 100%;
  box-shadow: inset 0px -1px 0px ${defaultTheme.colors.greyMedium};

  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: flex;
    padding: 28px 16px;
    justify-content: space-around;
  }
`

interface ColumnProps {
  clickable?: boolean
  noUppercase?: boolean
}

const Column = styled.td<ColumnProps>`
  color: ${defaultTheme.colors.columnBorder};
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-transform: ${(p) => (p.noUppercase ? 'none' : 'uppercase')};
  padding-top: 10px;
  padding-bottom: 10px;
  text-align: ${(p) => (p.align ? p.align : 'center')};
  cursor: ${(p) => (p.clickable ? 'pointer' : null)};

  @media print {
    font-size: 10px;
  }
`

interface StyledTextProps {
  bold?: boolean
  underline?: boolean
  lineThrough?: boolean
  pointer?: boolean
  block?: boolean
  wrap?: boolean
}
const StyledText = styled.span<StyledTextProps>`
  display: ${(p) => (p.block || p.wrap ? 'block' : 'inline')};
  font-family: Roboto, serif;
  font-weight: ${(p) => (p.bold ? 'bold' : 'normal')};
  text-decoration: ${(p) =>
    [p.underline && 'underline', p.lineThrough && 'line-through']
      .filter(Boolean)
      .join(' ') || 'none'};
  cursor: ${(p) => (p.pointer ? 'pointer' : 'initial')};
  overflow: hidden;
  text-overflow: ${(p) => (p.wrap ? 'clip' : 'ellipsis')};
  white-space: ${(p) => (p.wrap ? 'normal' : 'nowrap')};
  overflow-wrap: ${(p) => (p.wrap ? 'anywhere' : 'normal')};
  word-break: ${(p) => (p.wrap ? 'break-word' : 'normal')};
  max-width: 100%;
  font-size: 14px;
  color: black;
  text-transform: none;

  @media screen and (max-width: ${breakpoints.tablet}px) {
    width: ${(p) => (p.wrap ? '100%' : '70%')};
    text-overflow: ${(p) => (p.wrap ? 'clip' : 'ellipsis')};
    white-space: ${(p) => (p.wrap ? 'normal' : 'nowrap')};
    font-weight: 500;
  }

  @media print {
    text-decoration: none;
    font-weight: normal;
    font-size: 10px;
  }
`

const LabelWrapper = styled.div`
  display: flex;
  align-text: left;
  align-items: center;
  margin-top: 5px;
  text-transform: none;
  color: black;

  @media screen and (max-width: ${breakpoints.tablet}px) {
    font-weight: normal;
  }

  @media print {
    font-weight: normal;
    width: auto;
    font-size: 10px;
  }
`

const LabelWrapperMobile = styled.div`
  display: flex;
  color: ${defaultTheme.colors.columnBorder};
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  text-transform: capitalize;
  line-height: 2;
  @media print {
    display: none;
  }
`

const RightContentSpan = styled.span`
  @media screen and (max-width: ${breakpoints.tablet}px) {
    margin-right: 3px;
    font-weight: 500;
  }
`

const I18nComponent = styled(I18n)`
  @media print {
    font-size: 10px !important;
  }
`

const DetailsValueMobile = styled.div`
  display: none;
  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: block;
  }
`

const StyledTextMobile = styled.span`
  margin-left: 2px;

  @media print {
    display: none;
  }
`

const LeftCrossMobile = styled.span`
  margin-right: 2px;
`
const ColumnDesktop = styled(Column)`
  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: none;
  }
`
const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`
const PreviousPrice = styled.span`
  text-decoration: line-through;
  font-weight: normal;
`
const MobileBundleWrapper = styled.div`
  margin-top: -4px;
  @media print {
    display: none;
  }
`
const LocationCell = styled.div`
  display: flex;
  align-items: flex-start;
  height: 100%;
`
